import {makeAutoObservable} from 'mobx'
import {Time, Util} from 'yougile-util'
import {User} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {Refresh} from '@client/refresh'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {globals} from '@extensions/globals-base'

interface State {
  show: boolean
  companyName: string
  userName: string
  phone: string
  errors: {
    companyName: boolean
    userName: boolean
    phone: boolean
  }
}

const maxTime = 10 * Time.day

export class RegistrationFinishStore {
  state: State = {
    show: false,
    companyName: '',
    userName: '',
    phone: '',
    errors: {
      companyName: false,
      userName: false,
      phone: false
    }
  }

  constructor() {
    makeAutoObservable(this)
    this.state.userName = Accounts.getName(globals.currentUserId)
    this.state.show = this.shouldBeShown()
  }

  private shouldBeShown(): boolean {
    const creationTime = this.getCurrentUser().timestamp
    return (
      globals.registrationType === 'google' &&
      !globals.phone &&
      creationTime > Time.now() - maxTime
    )
  }

  private getCurrentUser(): User {
    return Sync.getProfile().getUser(globals.currentUserId)
  }

  changeName(value: string): void {
    this.state.userName = value
    this.state.errors.userName = false
  }

  changeCompany(value: string): void {
    this.state.companyName = value
    this.state.errors.companyName = false
  }

  changePhone(value: string): void {
    this.state.phone = value
    this.state.errors.phone = false
  }

  get isCorrectName(): boolean {
    return this.state.userName.length >= 3
  }

  get isCorrectCompany(): boolean {
    return this.state.companyName.length >= 3
  }

  get isCorrectPhone(): boolean {
    return this.state.phone.replace(/[^0-9]/g, '').length >= 11
  }

  submit(): void {
    this.state.errors.companyName = !this.isCorrectCompany
    this.state.errors.userName = !this.isCorrectName
    this.state.errors.phone = !this.isCorrectPhone
    if (this.isCorrectCompany && this.isCorrectName && this.isCorrectPhone) {
      this.sendData()
      globals.phone = this.state.phone
      this.state.show = false
      void Tracking.send(TrackingEvent.regFormClick)
      void Tracking.send(TrackingEvent.regFormSuccess)
      void Tracking.send(TrackingEvent.regPageCompleteRegister)
    }
  }

  private sendData(): void {
    const box = Sync.getCurrentSandbox()
    const company = box.getHub(box.companyId)
    box.editTitle(company, this.state.companyName)
    const account = Accounts.get(globals.currentUserId)
    account.realName = this.state.userName
    void Accounts.update(account)
    void Backend.postJSON('/data/register/set-phone', {
      ...Backend.loadCred(),
      phone: this.state.phone
    })
  }

  async sendTestRequest(): Promise<void> {
    const res = await Backend.postJSON('/data/register/test-popup', {
      ...Backend.loadCred()
    })
    if (res.result === 'ok') {
      const user = this.getCurrentUser()
      user.timestamp = Time.now()
      user.commit()
      await Util.wait(1000)
      Refresh.clearCredentials()
    }
  }
}
